import {useState, useEffect, useCallback} from 'react'
import {Dispatch} from 'redux'
import * as Yup from 'yup'
import {ErrorMessage, Field, Form, Formik} from 'formik'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {AuthSlices} from '../../../setup/redux/auth/slices'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import {RootState} from '../../../setup'
import {PageTitle} from '../../../_metronic/layout/core'
import {Input} from '../../../_metronic/_partials/controls'
import {getRequest, postRequest} from '../../../setup/redux/common/cruds'
import {Loader} from '../../services/Loader'
import {serialize} from 'object-to-formdata'
import {
  handleNotificationWithToastify,
  resetLocalStorageData,
  validatePhoneField,
} from '../../services/Functional'
import {getUserDisplayEmail} from '../../services/userEmailDisplay'
import {FieldEditDialog} from '../../../_metronic/dialogs/FieldEditDialog'
import {getUserAccess, getUserById} from '../../../setup/redux/auth/actions'
import FAIcon from '../../../_metronic/helpers/components/FAIcon'
import {faImage, faEnvelope, faPlus} from '@fortawesome/free-solid-svg-icons'
import Dialog from '../../../_metronic/dialogs/CommonDialog'
import CommonDialogBody from '../../../_metronic/dialogs/CommonDialogBody'
import {Button, Modal} from 'react-bootstrap'
import {requestAccountDeletion} from '../../../setup/redux/data_anonymization/actions'
import {CustomOtpInput} from '../auth/components/otpinput/CustomOtpInput'

const initRequest = {
  name: '',
  username: '',
  email: '',
  country_code: '',
  phone: '',
  avatar: undefined,
  is_two_factor_auth: true,
  internal_notes: '',
}
const {actions} = AuthSlices
const ProfileSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Please enter full name')
    .max(50, 'Max 50 character allowed')
    // .matches(
    //   /^[A-Za-zΑ-Ωα-ωΆ-Ώά-ώ]+(?:\s[A-Za-zΑ-Ωα-ωΆ-Ώά-ώ]+)*(?:['_.\s][A-Za-zΑ-Ωα-ωΆ-Ώά-ώ]+)*$/,
    //   'Full name is not valid. Ex John Doe'
    // )
    .required('Full name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
  phone: Yup.string()
    .min(8, 'Phone must be at least 8 digits')
    .required('Phone is required')
    .test('verifyPhoneValidation', 'Phone number is not valid', function (value) {
      const {country_code} = this.parent as {country_code: string}
      return validatePhoneField(value || '', country_code)
    }),
  avatar: Yup.mixed()
    .nullable()
    .test('fileFormat', 'Only JPG and PNG formats are allowed', (value) => {
      // If no file is provided or the value is a string (e.g., a URL), consider it valid
      if (!value || typeof value === 'string') {
        return true
      }
      // Check if the file type matches the allowed formats
      const allowedFormats = ['image/jpg', 'image/jpeg', 'image/png']
      // Ensure value is an object with a `type` property
      if (typeof value === 'object' && value !== null && 'type' in value) {
        const file = value as {type: string} // Explicitly cast value to an object with a `type` property
        return allowedFormats.includes(file.type)
      }
      return false // Fallback for invalid cases
    }),
})

export const UpdateProfile = () => {
  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()
  const user: any = useSelector<RootState>((state) => state.auth.user)
  const [loading, setLoading] = useState<boolean>(false)
  const [isOpenEmailEditDialog, setIsOpenEmailEditDialog] = useState(false)
  const [isOpenMobileNumberEditDialog, setIsOpenMobileNumberEditDialog] = useState(false)
  const [isOpenDeleteAccountDialog, setIsOpenDeleteAccountDialog] = useState(false)
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false)
  const [deleteRequestSubmitted, setDeleteRequestSubmitted] = useState(false)
  const [userEmails, setUserEmails] = useState<any[]>([])
  const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(false)
  const [addEmailStep, setAddEmailStep] = useState<0 | 1 | 2>(0)
  const [newEmailValue, setNewEmailValue] = useState('')
  const [emailOtp, setEmailOtp] = useState('')
  const [emailOtpError, setEmailOtpError] = useState(false)
  const [addEmailLoading, setAddEmailLoading] = useState(false)

  const API_URL = process.env.REACT_APP_API_BASE_URL

  const fetchUserEmails = useCallback(async () => {
    try {
      const resp = await getRequest(`${API_URL}auth/me/emails`)
      if (resp?.data?.success) {
        const emails = resp.data.data?.emails || resp.data.data || []
        setUserEmails(emails)
        dispatch(
          actions.updateUser({
            user: {
              emails,
              email: getUserDisplayEmail({ emails, email: user?.email }),
            },
          }),
        )
      }
    } catch {
      /* silent */
    }
  }, [API_URL, dispatch, user?.email])

  const handleInitiateAddEmail = async () => {
    if (!newEmailValue) return
    setAddEmailLoading(true)
    try {
      const resp = await postRequest(`${API_URL}auth/me/emails`, {email: newEmailValue} as any)
      if (resp?.data?.success) {
        handleNotificationWithToastify(resp.data)
        setAddEmailStep(1)
      } else {
        handleNotificationWithToastify(resp.data)
      }
    } catch (e: any) {
      handleNotificationWithToastify(e?.response?.data || {success: false, message: 'Failed to send OTP'})
    } finally {
      setAddEmailLoading(false)
    }
  }

  const handleConfirmAddEmail = async () => {
    if (emailOtp.length < 6) return
    setAddEmailLoading(true)
    try {
      const resp = await postRequest(`${API_URL}auth/me/emails/verify`, {email: newEmailValue, otp: emailOtp} as any)
      if (resp?.data?.success) {
        handleNotificationWithToastify(resp.data)
        setAddEmailStep(2)
        fetchUserEmails()
        setTimeout(() => {
          setIsAddEmailDialogOpen(false)
          setAddEmailStep(0)
          setNewEmailValue('')
          setEmailOtp('')
        }, 3000)
      } else {
        setEmailOtpError(true)
        handleNotificationWithToastify(resp.data)
        setTimeout(() => { setEmailOtpError(false); setEmailOtp('') }, 3000)
      }
    } catch (e: any) {
      setEmailOtpError(true)
      handleNotificationWithToastify(e?.response?.data || {success: false, message: 'Verification failed'})
      setTimeout(() => { setEmailOtpError(false); setEmailOtp('') }, 3000)
    } finally {
      setAddEmailLoading(false)
    }
  }

  const handleCloseAddEmailDialog = () => {
    setIsAddEmailDialogOpen(false)
    setAddEmailStep(0)
    setNewEmailValue('')
    setEmailOtp('')
    setEmailOtpError(false)
  }

  useEffect(() => {
    fetchUserEmails()
  }, [fetchUserEmails])

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true)
      try {
        const response: any = await dispatch(getUserById(user.sub || user.id, 'show'))
        if (
          response?.data?.data?.user?.target_data_anonymization_requests &&
          Array.isArray(response.data.data.user.target_data_anonymization_requests)
        ) {
          const hasPendingRequest = response.data.data.user.target_data_anonymization_requests.some(
            (request: any) => request.status === 'pending'
          )
          if (hasPendingRequest) {
            setDeleteRequestSubmitted(true)
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUserData()
  }, [user])

  const saveRequest = (values: any) => {
    setLoading(true)
    postRequest(`${process.env.REACT_APP_API_BASE_URL}auth/update-profile`, values, {
      'Content-Type': 'multipart/form-data',
    })
      .then((response) => {
        setLoading(false)
        handleNotificationWithToastify(response.data)
        if (response.data.success) {
          dispatch(actions.updateUser(response.data.data))
          navigate('/dashboard')
        }
      })
      .catch((er) => {
        setLoading(false)
      })
  }

  useEffect(() => {
    resetLocalStorageData()
  }, [])

  const userdata = {
    id: user.sub || user.id,
    name: user.name,
    username: user.username,
    email: getUserDisplayEmail(user),
    phone: user.phone,
    country_code: user.country_code,
    internal_notes: user.internal_notes,
    avatar: user.avatar,
    is_two_factor_auth: user.is_two_factor_auth,
  }

  const handleEmailEdit = () => {
    setIsOpenEmailEditDialog(true)
  }

  const handleCloseDialog = () => {
    resetLocalStorageData()
    setIsOpenEmailEditDialog(false)
    setIsOpenMobileNumberEditDialog(false)
  }

  const handlePhoneEdit = () => {
    setIsOpenMobileNumberEditDialog(true)
  }

  const handleverifiedOTP = async () => {
    await dispatch(getUserAccess())
    // Refresh user profile so the updated phone/email appears immediately without a page reload
    try {
      const response: any = await dispatch(getUserById(user.sub || user.id, 'show'))
      if (response?.data?.data?.user) {
        dispatch(actions.updateUser({user: response.data.data.user}))
      }
    } catch {
      /* silent */
    }
    await fetchUserEmails()
  }

  const handleDeleteAccount = async () => {
    setDeleteAccountLoading(true)
    try {
      const response: any = await dispatch(requestAccountDeletion({request_type: 'USER'}))
      if (response?.success) {
        setIsOpenDeleteAccountDialog(false)
        setDeleteRequestSubmitted(true)
      }
    } catch (error) {
      handleNotificationWithToastify({
        success: false,
        message: 'Failed to submit account deletion request',
      })
    } finally {
      setDeleteAccountLoading(false)
    }
  }

  const handleCloseDeleteAccountDialog = () => {
    setIsOpenDeleteAccountDialog(false)
  }

  return (
    <>
      {loading && <Loader />}
      <PageTitle>Manage Profile</PageTitle>
      <Formik
        enableReinitialize={true}
        initialValues={userdata || initRequest}
        validationSchema={ProfileSchema}
        onSubmit={(values) => {
          saveRequest(serialize(values))
        }}
      >
        {({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => (
          <>
            <Form className='form'>
              <div className='card gutter-b'>
                <div className='card-body pb-0'>
                  <div className='row'>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label'>
                          Full Name <span className='text-danger'>*</span>{' '}
                        </label>
                        <Field name='name' component={Input} placeholder='Ex. John Doe' />
                        <ErrorMessage name='name' component='div' className='text-danger mt-2' />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label'>Username </label>
                        <Field name='username' component={Input} placeholder='Enter Username' />
                        <ErrorMessage
                          name='username'
                          component='div'
                          className='text-danger mt-2'
                        />
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label'>
                          Email <span className='text-danger'>*</span>
                        </label>
                        <Field
                          type='email'
                          name='email'
                          component={Input}
                          placeholder='mail@website.com'
                          disabled
                        />
                        <div className='d-flex justify-content-between mt-2'>
                          <ErrorMessage name='email' component='div' className='text-danger' />
                          <a onClick={handleEmailEdit} className='link-primary text-end w-100'>
                            <u>Change Email</u>
                          </a>
                        </div>
                      </div>
                    </div>
                    {/* Associated Emails (A23) */}
                    <div className='col-md-12'>
                      <div className='form-group mb-4'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <label className='form-label mb-0'>Associated Emails</label>
                          <a
                            onClick={() => setIsAddEmailDialogOpen(true)}
                            className='link-primary text-end cursor-pointer'
                          >
                            <FAIcon icon={faPlus} className='me-1' />
                            Add Email
                          </a>
                        </div>
                        <div className='d-flex flex-column gap-2'>
                          {userEmails.length > 0 ? (
                            userEmails.map((ue: any, idx: number) => (
                              <div
                                key={ue.id || idx}
                                className='d-flex align-items-center gap-3 py-2 px-3 rounded'
                                style={{
                                  backgroundColor: ue.is_primary ? '#f0f4ff' : '#f8f9fa',
                                  border: `1px solid ${ue.is_primary ? '#c7d7f5' : '#e2e8f0'}`,
                                }}
                              >
                                <FAIcon icon={faEnvelope} className='text-muted fs-6' />
                                <span className='text-gray-700 fs-7 flex-grow-1'>{ue.email}</span>
                                {ue.is_primary && (
                                  <span className='badge badge-light-primary fs-9 py-1 px-2'>
                                    Primary
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <span className='text-muted fs-7'>No associated emails found</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='col-md-4'>
                      <div className='form-group'>
                        <label className='form-label'>
                          Phone <span className='text-danger'>*</span>
                        </label>
                        <PhoneInput
                          buttonClass='flag_dropdown'
                          dropdownStyle={{backgroundColor: 'none'}}
                          enableSearch={true}
                          disableSearchIcon={true}
                          searchStyle={{width: '97%', margin: '0'}}
                          countryCodeEditable={false}
                          disableDropdown={true}
                          disabled
                          country='in'
                          value={(user.country_code || '') + (user.phone || '')}
                          inputProps={{
                            name: 'phone',
                            id: 'phone',
                            required: true,
                          }}
                          onChange={(phone: any, data: any) => {
                            handleChange({
                              target: {name: 'country_code', value: data.dialCode},
                            })
                            let phone_number = phone.slice(data.dialCode.length)
                            handleChange({
                              target: {name: 'phone', value: phone_number},
                            })
                          }}
                          onBlur={() => {
                            handleBlur({target: {name: 'phone'}})
                          }}
                        />
                        <div className='d-flex justify-content-between mt-2'>
                          <ErrorMessage name='phone' component='div' className='text-danger mt-2' />
                          <a onClick={handlePhoneEdit} className='link-primary text-end w-100'>
                            <u>Change Phone</u>
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='form-group'>
                        <div className='d-flex justify-content-between'>
                          <label className='form-label' htmlFor='logo'>
                            Avatar
                          </label>
                          {values.avatar && typeof values.avatar === 'string' && (
                            <a href={values.avatar} rel='noreferrer' target='_blank'>
                              <FAIcon icon={faImage} className='fa-lg' />
                            </a>
                          )}
                        </div>
                        <input
                          type='file'
                          className='form-control'
                          id='avatar'
                          name='avatar'
                          accept='image/*'
                          onChange={(event) => {
                            // @ts-ignore
                            setFieldValue('avatar', event.target.files[0])
                          }}
                        />
                        <ErrorMessage name='avatar' component='div' className='text-danger mt-2' />
                      </div>
                    </div>
                    <div className='col-md-12'>
                      <div className='form-group'>
                        <label className='form-label'>Internal Notes</label>
                        <Field
                          name='internal_notes'
                          as='textarea'
                          className='form-control'
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='card gutter-b'>
                <div className='card-body pb-0'>
                  <div className='row'>
                    <div className='col-md-12'>
                      <div className='form-group'>
                        <button
                          type='submit'
                          onSubmit={() => handleSubmit}
                          className='btn btn-primary'
                        >
                          Update
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delete Account Section */}
              <div className='card gutter-b'>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-12'>
                      <div className='form-group m-0'>
                        <div className='d-flex flex-column align-items-start'>
                          <button
                            type='button'
                            className={`btn btn-link text-danger p-0 mb-2 text-decoration-none fw-semibold ${
                              deleteRequestSubmitted ? 'opacity-50' : ''
                            }`}
                            onClick={() =>
                              !deleteRequestSubmitted && setIsOpenDeleteAccountDialog(true)
                            }
                            disabled={deleteAccountLoading || deleteRequestSubmitted}
                          >
                            Delete Account
                          </button>
                          <span className='text-muted fst-italic small'>
                            {deleteRequestSubmitted
                              ? 'Your request has been shared with the Admin, currently under review.'
                              : 'Raise a request to the Admin to delete your information'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
      {isOpenEmailEditDialog && (
        <FieldEditDialog
          isOpenDialog={isOpenEmailEditDialog}
          mode='email'
          handleClose={handleCloseDialog}
          stepStartingNumber={0}
          handleverifiedOTP={handleverifiedOTP}
        />
      )}
      {isOpenMobileNumberEditDialog && (
        <FieldEditDialog
          isOpenDialog={isOpenMobileNumberEditDialog}
          mode='mobile'
          handleClose={handleCloseDialog}
          stepStartingNumber={0}
          handleverifiedOTP={handleverifiedOTP}
        />
      )}

      {/* Add Email Dialog (A23) */}
      <Modal
        show={isAddEmailDialogOpen}
        onHide={handleCloseAddEmailDialog}
        centered
        backdrop='static'
        size='lg'
        className='custom-dialog'
      >
        <Modal.Header className='border-0 pb-0' closeButton></Modal.Header>
        <Modal.Body className='pb-12 pt-0'>
          <Modal.Title className='fs-1 text-center custom-dialog-title pb-6'>
            {addEmailStep === 2 ? 'Email Added' : 'Add Email'}
          </Modal.Title>
          {addEmailStep === 0 && (
            <div className='pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
              <div className='text-muted text-center'>
                Enter a new email address to associate with your account.
              </div>
              <div className='form-group m-0 w-75'>
                <input
                  type='email'
                  placeholder='New Email Address'
                  autoComplete='off'
                  className='form-control'
                  value={newEmailValue}
                  onChange={(e) => setNewEmailValue(e.target.value)}
                />
              </div>
              <button
                type='button'
                disabled={!newEmailValue || addEmailLoading}
                onClick={handleInitiateAddEmail}
                className='btn btn-primary btn-pill w-75'
              >
                {addEmailLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          )}
          {addEmailStep === 1 && (
            <div className='pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
              <div className='text-muted text-center'>
                Enter the 6-digit code sent to <strong>{newEmailValue}</strong>
              </div>
              <CustomOtpInput
                label='email-otp'
                otp={emailOtp}
                error={emailOtpError}
                handleOTPChange={(otp: string) => setEmailOtp(otp)}
                className='dialog-otp-input'
                separatorClassName='dialog-otp-separator'
              />
              <button
                type='button'
                disabled={emailOtp.length < 6 || addEmailLoading}
                onClick={handleConfirmAddEmail}
                className='btn btn-primary btn-pill w-75'
              >
                {addEmailLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          )}
          {addEmailStep === 2 && (
            <div className='pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
              <img className='verified' src='/media/verified.gif' alt='Verified' style={{width: 80}} />
              <div className='text-muted text-center'>
                <strong>{newEmailValue}</strong> has been added to your account.
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Dialog
        show={isOpenDeleteAccountDialog}
        onHide={handleCloseDeleteAccountDialog}
        body={
          <CommonDialogBody
            title='Are you sure you want to delete your account?'
            text='This action is irreversible. Your account deletion request will be submitted for administrator approval.'
          />
        }
        footer={
          <div className='d-flex flex-column-reverse flex-lg-row align-items-center justify-content-center gap-4 w-100 m-0 pt-0'>
            <Button
              variant='secondary'
              onClick={handleCloseDeleteAccountDialog}
              className='dialog-btn dialog-btn-secondary'
              disabled={deleteAccountLoading}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleDeleteAccount}
              className='dialog-btn btn btn-primary'
              disabled={deleteAccountLoading}
            >
              Delete Account
            </Button>
          </div>
        }
        modalClassName='delete-dialog'
        headerClassname='border-0 p-0'
        size='lg'
        centered
        backdrop='static'
        keyboard={false}
      />
    </>
  )
}
